import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

const contactFormSchema = z.object({
  name: z.string().min(1, "Name is required.").max(100),
  email: z
    .string()
    .min(1, "Email address is required.")
    .max(254)
    .email("Enter a valid email address, such as name@example.com."),
  phone: z.string().max(30).optional().or(z.literal("")),
  company: z.string().max(100).optional().or(z.literal("")),
  message: z.string().min(1, "Message is required.").max(1000),
  // Honeypot field — must remain empty. Bots fill it, humans don't.
  website: z.string().max(500).optional().or(z.literal("")),
});

type ContactFormValues = z.infer<typeof contactFormSchema>;

export function ContactForm() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submittedName, setSubmittedName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      company: "",
      message: "",
      website: "",
    },
  });

  const onSubmit = async (data: ContactFormValues) => {
    // Honeypot: if filled, silently pretend success without calling the API.
    if (data.website && data.website.trim() !== "") {
      setSubmittedName(data.name.trim());
      setSubmitted(true);
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");

    try {
      const { website, ...payload } = data;
      const response = await fetch(
        import.meta.env.VITE_CONTACT_API_URL ||
          "https://vmdeknus6uaot7x6kqwz3gckjm0gecma.lambda-url.us-east-1.on.aws/",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to submit form. Please try again.");
      }

      setSubmittedName(data.name.trim());
      setSubmitted(true);
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : "An unexpected error occurred. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const nameInput = (e.target as HTMLFormElement).elements.namedItem("name") as HTMLInputElement;
    const nameValue = nameInput?.value?.trim() ?? "";

    if (!nameValue) {
      window.alert("Please enter your name.");
      return;
    }

    handleSubmit(onSubmit)();
  };

  const handleNewRequest = () => {
    reset();
    setSubmitted(false);
    setSubmittedName("");
  };

  const handleDone = () => {
    navigate("/");
  };

  if (submitted) {
    return (
      <div className="mx-auto w-full max-w-[760px] rounded-3xl border border-[#E8E4DE] bg-white p-7 sm:p-10">
        <div className="flex flex-col items-center px-5 py-9">
          <div className="flex h-[58px] w-[58px] items-center justify-center rounded-full bg-[#FCE8E4]">
            <span className="text-[28px] font-extrabold text-[#BC0319]">✓</span>
          </div>
          <h2 className="mt-5 text-center text-[25px] font-extrabold leading-8 text-[#1F2A3A]">
            Thank you, {submittedName}.
          </h2>
          <p className="mt-3 max-w-[520px] text-center text-sm leading-[23px] text-[#5A6472]">
            Your message has been sent. We'll get back to you as soon as possible.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={handleNewRequest}
              className="min-h-[46px] rounded-full border border-[#D8D5CE] bg-white px-[22px] text-sm font-bold text-[#1F2A3A] transition hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1F2A3A]"
            >
              Send another message
            </button>
            <button
              type="button"
              onClick={handleDone}
              className="min-h-[46px] rounded-full bg-[#BC0319] px-6 text-sm font-extrabold text-white transition hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[760px] rounded-3xl border border-[#E8E4DE] bg-white p-7 sm:p-10">
      <div className="mb-6">
        <p className="text-xs font-extrabold uppercase tracking-wide text-[#BC0319]">CONTACT</p>
        <h1 className="mt-1.5 text-[28px] font-extrabold leading-[34px] tracking-tight text-[#1F2A3A]">
          Get in touch
        </h1>
        <p className="mt-2.5 max-w-[620px] text-sm leading-[22px] text-[#5A6472]">
          Have questions about Motosport Software? Send us a message and we'll respond as soon as
          possible.
        </p>
      </div>

      <form onSubmit={handleFormSubmit} noValidate className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="contact-name"
              className="text-[13px] font-bold leading-[18px] text-[#1F2A3A]"
            >
              Name{" "}
              <span aria-hidden="true" className="text-[#BC0319]">
                *
              </span>
            </label>
            <input
              id="contact-name"
              type="text"
              maxLength={100}
              aria-required="true"
              {...register("name")}
              className="min-h-[48px] rounded-xl border border-[#D8D5CE] bg-[#FAF8F3] px-3.5 py-3 text-sm leading-5 text-[#1F2A3A] outline-none focus-visible:ring-2 focus-visible:ring-[#1F2A3A]"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="contact-email"
              className="text-[13px] font-bold leading-[18px] text-[#1F2A3A]"
            >
              Email{" "}
              <span aria-hidden="true" className="text-[#BC0319]">
                *
              </span>
            </label>
            <input
              id="contact-email"
              type="email"
              maxLength={254}
              aria-required="true"
              aria-describedby={errors.email ? "contact-email-error" : undefined}
              {...register("email")}
              className={`min-h-[48px] rounded-xl border px-3.5 py-3 text-sm leading-5 text-[#1F2A3A] outline-none focus-visible:ring-2 focus-visible:ring-[#1F2A3A] ${
                errors.email ? "border-[#BC0319] bg-[#FFF7F7]" : "border-[#D8D5CE] bg-[#FAF8F3]"
              }`}
            />
            {errors.email && (
              <p
                id="contact-email-error"
                className="text-xs leading-[17px] text-[#BC0319]"
                role="alert"
              >
                {errors.email.message}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="contact-phone"
              className="text-[13px] font-bold leading-[18px] text-[#1F2A3A]"
            >
              Phone
            </label>
            <input
              id="contact-phone"
              type="tel"
              maxLength={30}
              {...register("phone")}
              className="min-h-[48px] rounded-xl border border-[#D8D5CE] bg-[#FAF8F3] px-3.5 py-3 text-sm leading-5 text-[#1F2A3A] outline-none focus-visible:ring-2 focus-visible:ring-[#1F2A3A]"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="contact-company"
              className="text-[13px] font-bold leading-[18px] text-[#1F2A3A]"
            >
              Company
            </label>
            <input
              id="contact-company"
              type="text"
              maxLength={100}
              {...register("company")}
              className="min-h-[48px] rounded-xl border border-[#D8D5CE] bg-[#FAF8F3] px-3.5 py-3 text-sm leading-5 text-[#1F2A3A] outline-none focus-visible:ring-2 focus-visible:ring-[#1F2A3A]"
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="contact-message"
            className="text-[13px] font-bold leading-[18px] text-[#1F2A3A]"
          >
            Message{" "}
            <span aria-hidden="true" className="text-[#BC0319]">
              *
            </span>
          </label>
          <textarea
            id="contact-message"
            maxLength={1000}
            rows={5}
            aria-required="true"
            aria-describedby={errors.message ? "contact-message-error" : undefined}
            {...register("message")}
            className={`min-h-[118px] rounded-xl border px-3.5 py-3 text-sm leading-5 text-[#1F2A3A] outline-none focus-visible:ring-2 focus-visible:ring-[#1F2A3A] ${
              errors.message ? "border-[#BC0319] bg-[#FFF7F7]" : "border-[#D8D5CE] bg-[#FAF8F3]"
            }`}
          />
          {errors.message && (
            <p
              id="contact-message-error"
              className="text-xs leading-[17px] text-[#BC0319]"
              role="alert"
            >
              {errors.message.message}
            </p>
          )}
        </div>

        {/* Honeypot field — hidden from real users, bait for bots. */}
        <div
          aria-hidden="true"
          className="absolute left-[-9999px] top-[-9999px] h-0 w-0 overflow-hidden"
        >
          <label htmlFor="contact-website">Website (leave blank)</label>
          <input
            id="contact-website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            {...register("website")}
          />
        </div>

        <div className="flex flex-col gap-4 pt-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs leading-[18px] text-[#5E6673]" aria-hidden="true">
            <span className="text-[#BC0319]">*</span> = required
          </p>
          <div className="flex items-center justify-end gap-3">
            <button
              type="submit"
              disabled={isSubmitting}
              className="min-h-[46px] rounded-full bg-[#BC0319] px-6 text-sm font-extrabold text-white transition hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Sending..." : "Send message"}
            </button>
          </div>
        </div>
        {submitError && (
          <div
            className="rounded-lg bg-[#FFF7F7] border border-[#BC0319] p-4 text-sm text-[#BC0319]"
            role="alert"
          >
            {submitError}
          </div>
        )}
      </form>
    </div>
  );
}
